<template>
  <v-select 
    autocomplete="ftsSelect"
    :append-to-body="appendToBody"
    :calculate-position="withPopper"
    :label="label"
    :loading="loading"
    :multiple="multiple"
    :options="infiniteScroll ? paginated : options"
    :reduce="getReduce"
    v-model="model"
    :placeholder="disabled ? null : placeholder"
    :filterable="infiniteScroll ? false : filterable"
    :filter-by="isFilterKeys ? filterByKeys : filterBy"
    :getOptionKey="getOptionKey"
    :selectable="getSelectable"
    :no-drop="disabled"
    :clearable="disabled ? false : clearable"
    :clearSearchOnBlur="clearSearchOnBlur"
    :clearSearchOnSelect="clearSearchOnSelect"
    :closeOnSelect="closeOnSelect"
    :taggable="taggable"
    :create-option="createOption"
    :disabled="disabled"
    class="fts-select"
    :class="[
      {'vs--error': error},
      {'comfort-design': comfortDesign}
    ]"
    @input="onInput"
    @open="onOpen"
    @close="onClose"
    @search="onSearch"
    @search:focus="onSearchFocus"
    @search:blur="onSearchBlur"
    @option:selected="optionSelected"
    ref="ftsSelect"
  >
    <!-- selected option -->
    <template #selected-option-container="{ option, deselect, multiple, disabled }">
      <div class="selected-option" :class="{ 'multiple': multiple }">  
        <slot name="selected-option-container" :option="option">
          <div>{{option[label]}}</div>
        </slot>
        <font-awesome-icon
          :icon="['fas', 'times']"
          :disabled="disabled"
          class="deselect-icon"
          @mousedown.stop="deselectOption(deselect, option)"
          v-if="multiple"
        />
      </div>
    </template>
    <!-- search -->
    <template #search="{ attributes, events }">
      <input class="vs__search fts-select-search" v-bind="attributes" v-on="events">
    </template>
    <!-- open indicator -->
    <template #open-indicator="{ attributes }">
      <slot name="open-indicator" :attributes="attributes" />
    </template>
    <!-- list header -->
    <template #list-header="{ search, loading, searching, filteredOptions }">
      <slot
        name="list-header"
        :search="search"
        :loading="loading"
        :searching="searching"
        :filteredOptions="filteredOptions"
        v-if="filteredOptions && filteredOptions.length"
      />
    </template>
    <!-- list footer -->
    <template #list-footer="{ search, loading, searching, filteredOptions }">
      <li
        class="fts-select__infinite-scroll-loader"
        ref="load" 
        v-show="hasNextPage"
        v-if="infiniteScroll"
      >
        {{infiniteScrollLoaderText}}
      </li>
      <slot
        name="list-footer"
        :search="search"
        :loading="loading"
        :searching="searching"
        :filteredOptions="filteredOptions"
        v-if="filteredOptions && filteredOptions.length"
      />
    </template>
    <!-- option -->
    <template #option="option">
      <slot name="option" :option="option" :select="optionSelected"/>
    </template>
    <!-- no options -->
    <template #no-options="{ search, searching, loading }">
      <slot
        name="no-options"
        :search="search"
        :searching="searching"
        :loading="loading"
      >
        <div class="search-no-options">
          <!-- no match option -->
          <div class="search-no-options-wrapper " v-if="search">
            <font-awesome-icon
              :icon="['fas', 'search']"
              class="search-no-options-icon"
            />
            <div>
              {{noMatchOptionsText}}
            </div>
          </div>
          <!-- no option -->
          <div class="search-no-options-wrapper" v-else>
            <font-awesome-icon
              :icon="['fas', 'exclamation-circle']"
              class="search-no-options-icon"
            />
            <div>
              {{noOptionsText}}
            </div>
          </div>
          <!-- footer -->
          <slot
            name="no-options-footer"
            :search="search"
            :searching="searching"
            :loading="loading"
          />
        </div>
      </slot>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select';
import "vue-select/dist/vue-select.css";
import { createPopper } from '@popperjs/core';
import sortAndStringify from '../commons/sortAndStringify';

const infiniteScrollObserve = (observer, element) => {
  if (observer && element) {
    observer.observe(element);
  }
}

const infiniteScrollDisconnect = (observer) => {
  observer && observer.disconnect();
}

export default {
  props: {
    appendToBody: {
      type: Boolean,
      default: true
    },
    label: {
      type: String,
      default: "name"
    },
    reduce: Function,
    reduceKey: {
      type: String,
      default: "id"
    },
    multiple: Boolean,
    formData: {
      type: Object
    },
    item: {
      type: Object
    },
    value: {
      type: [Object, String, Number, Array,],
    },
    options: {
      type: Array
    },
    placeholder: {
      type: String,
      default: "Please select"
    },
    noMatchOptionsText: {
      type: String,
      default: "No matching options"
    },
    noOptionsText: {
      type: String,
      default: "No options found"
    },
    /**
     * not applicable if enabling `infiniteScroll`
     */
    filterable: {
      type: Boolean,
      default: true,
    },
    additionalFilterKeys: Array,
    getOptionKey: Function,
    selectable: Function,
    clearable: {
      type: Boolean,
      default: true
    },
    multipleClearSearchOnBlur: {
      type: Boolean,
      default: true
    },
    clearSearchOnBlur: {
      type: Function,
      default: function ({clearSearchOnSelect, multiple}) {
        try {
          return clearSearchOnSelect && (
            (multiple && this.$parent.multipleClearSearchOnBlur) || !multiple
          );
        } catch(err) {
          console.warn(`[fts-select]: 'clearSearchOnBlur' prop`, err);
          return clearSearchOnSelect && !multiple;
        }
      }
    },
    clearSearchOnSelect: {
      type: Boolean,
      default: true,
    },
    closeOnSelect: {
      type: Boolean,
      default: true
    },
    taggable: Boolean,
    createOption: Function,
    error: Boolean,
    disabled: Boolean,
    popperReferenceFor: String,
    popperPlacement: {
      type: String,
      default: 'bottom-start'
    },
    popperWidth: String,
    popperOffset: {
      type: Array,
      default: () => [0, 3]
    },
    popperPreventOverflowAltAxis: Boolean,
    popperPreventOverflowTetherOff: Boolean,
    comfortDesign: Boolean,
    /**
     * applicable if enabling `appendToBody`
     */
    appendDropdownClass: String,
    searchHandler: Function,
    infiniteScroll: Boolean,
    infiniteScrollLoaderText: {
      type: String,
      default: "Loading more options..."
    },
    pageSize: {
      type: Number,
      default: 20
    }
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  components: {
    "v-select": vSelect
  },
  data: () => ({
    loading: false,
    popperInstance: null,
    infiniteScrollObserver: null,
    totalCount: 20,
    search: ''
  }),
  methods: {
    withPopper(dropdownList, component, {width}) {
      dropdownList.style.width = this.popperWidth ? this.popperWidth : width;
      dropdownList.style.maxWidth = "100%";
      dropdownList.classList.add("fts-scrollbar");
      if (this.popperReferenceFor) {
        dropdownList.classList.add(...this.popperReferenceFor.split(" "));
      }
      if (this.appendDropdownClass) {
        dropdownList.classList.add(...this.appendDropdownClass.split(" "));
      }
      if (this.comfortDesign) {
        dropdownList.classList.add("comfort-design");
      }
      /* popper options */
      const options = {
        placement: this.popperPlacement,
        computeStyle: {
          gpuAcceleration: false
        },
        modifiers: [
        {
          name: 'offset', 
          options: {
            offset: this.popperOffset
          }
        },
        {
          name: 'preventOverflow', 
          options: {
            altAxis: this.popperPreventOverflowAltAxis, // default: false
            tether: !this.popperPreventOverflowTetherOff, // default: true
          }
        },
        {
          name: 'toggleClass',
          enabled: true,
          phase: 'write',
          fn ({state}) {
            component.$el.classList.toggle('drop-up', state.placement === 'top')
          },
        }]
      }
      /**
       * Here we position the dropdownList relative to the $refs.toggle Element.
       *
       * The 'offset' modifier aligns the dropdown so that the $refs.toggle and
       * the dropdownList overlap by 1 pixel.
       *
       * The 'toggleClass' modifier adds a 'drop-up' class to the Vue Select
       * wrapper so that we can set some styles for when the dropdown is placed
       * above.
       */
      this.popperInstance = createPopper(component.$refs.toggle, dropdownList, options);
      /**
       * To prevent memory leaks Popper needs to be destroyed.
       * If you return function, it will be called just before dropdown is removed from DOM.
       */
      return () => {
        if (this.popperInstance) {
          this.popperInstance.destroy();
          this.popperInstance = null;
        }
      }
    },
    onLoading(arg) {
      this.loading = arg;
    },
    onInput(val) {
      this.popperInstance && this.popperInstance.update();
      this.$emit("input", val);
    },
    async onOpen() {
      this.$emit("open", this.onLoading);
      if (this.infiniteScroll && !this.infiniteScrollObserver) {
        this.infiniteScrollObserver = new IntersectionObserver(this.infiniteScrollHandler);
      }

      if (typeof this.searchHandler === 'function') {
        try {
          this.loading = true;
          await this.searchHandler();
        } finally {
          this.loading = false;
        }
      }

      if (this.infiniteScroll && this.hasNextPage) {
        await this.$nextTick();
        infiniteScrollObserve(this.infiniteScrollObserver, this.$refs.load);
      }
    },
    onClose() {
      this.$emit("close");

      if (this.infiniteScroll) {
        this.totalCount = this.pageSize;
        infiniteScrollDisconnect(this.infiniteScrollObserver);
      }
    },
    onSearchHandler: _.debounce(async (search, loading, vm) => {
      try {
        loading(true);
        await vm.searchHandler(search);
      } finally {
        loading(false);
      }

      if (vm.infiniteScroll) {
        vm.totalCount = vm.pageSize;
        vm.search = search;
        await vm.$nextTick();
        infiniteScrollObserve(vm.infiniteScrollObserver, vm.$refs.load);
      }
    }, 500),
    async onSearch(searchText, loading) {
      this.$emit('search', {
        searchText: searchText,
        loading: loading
      });

      if (typeof this.searchHandler === 'function') {
        loading(true); // workaround
        await this.onSearchHandler(searchText, loading, this);
      } else if (this.infiniteScroll) {
        this.totalCount = this.pageSize;
        this.search = searchText;
        await this.$nextTick();
        infiniteScrollObserve(this.infiniteScrollObserver, this.$refs.load);
      }
    },
    onSearchFocus() {
      this.$emit('search:focus', this.onLoading);
    },
    onSearchBlur() {
      this.$emit('search:blur');
    },
    optionSelected(val) {
      this.$emit('option:selected', val);
    },
    deselectOption(deselect, option) {
      try {
        const reduce = this.getReduce(option);
        if (reduce === null || reduce === undefined) {
          const deselectOption = option[this.label];
          this.model = this.model.filter(x => x !== deselectOption);
        } else {
          deselect(option);
        }
      } catch(err) {
        console.warn(`[fts-select]: 'deselectOption'`, err);
        deselect(option);
      }
    },
    /**
     * infinite scroll
     */
    async infiniteScrollHandler([{ isIntersecting, target }]) {
      if (isIntersecting) {
        const ul = target.offsetParent;
        const scrollTop = target.offsetParent.scrollTop;
        this.totalCount += this.pageSize;
        await this.$nextTick();
        ul.scrollTop = scrollTop;
      }
    },
    filterBy(option, label, search) {
      return (
        (label || '')
          .toLocaleLowerCase()
          .indexOf(search.toLocaleLowerCase()) > -1
      );
    },
    filterByKeys(option, label, search) {
      if (this.filterBy(option, label, search))
        return true;

      return this.getFilterKeys.some(x => this.filterBy(option, option[x], search));
    }
   },
  computed: {
    model: {
      get() {
        const data = this.item && this.item.fieldName ? this.formData[this.item.fieldName] : this.value;
        if (this.multiple && Array.isArray(data)) {
          return data.filter(x => x !== null && x !== undefined);
        }
        return data;
      },
      set(val) {
        if (val !== null && val !== undefined) {
          if (this.item && this.item.fieldName)
            this.$set(this.formData, this.item.fieldName, val);
          else
            this.$emit('updateValue', val);
        } else {
          if (this.item && this.item.fieldName)
            this.$delete(this.formData, this.item.fieldName)
          else
            this.$emit('updateValue', val);
          
          this.$emit('option:clear');
        }
      }
    },
    getReduce() {
      return this.reduce ?
        this.reduce : 
        (x) => Object.prototype.toString.call(x) === '[object Object]' ?
          x[this.reduceKey] : x
      ;
    },
    getSelectable() {
      const getOptionKey = (option) => {
        if (this.getOptionKey && typeof this.getOptionKey === 'function') {
          try {
            return this.getOptionKey(option);
          } catch(err) { console.error(err); }
        }
        
        if (typeof option !== 'object') {
          return option;
        }

        try {
          return option && option.hasOwnProperty('id') ?
            option.id : sortAndStringify(option)
        } catch(err) {
          return console.error(err);
        }
      }
      const optionComparator = (a, b) => getOptionKey(a) === getOptionKey(b)

      return (option) => {
        if (this.selectable && typeof this.selectable === 'function') {
          try {
            if (!this.selectable(option)) {
              return false;
            }
          } catch(err) { console.error(err); }
        }
        if (this.model) {
          try {
            if (this.multiple) {
              if (
                Array.isArray(this.model) &&
                this.model.some((x) => optionComparator(x, this.getReduce(option)))
              ) {
                return false;
              }
            } else if (optionComparator(this.model, this.getReduce(option))) {
              return false;
            }
          } catch(err) { console.error(err); }
        }
        return true;
      }
    },
    getFilterKeys() {
      if (Array.isArray(this.additionalFilterKeys) && this.additionalFilterKeys.length > 0)
        return this.additionalFilterKeys;
    },
    isFilterKeys() {
      return Array.isArray(this.getFilterKeys) && this.getFilterKeys.length > 0;
    },
    /**
     * infinite scroll
     */
    filtered() {
      // vue-select
      const getOptionLabel = (option) => {
        if (typeof option === 'object') {
          if (!option.hasOwnProperty(this.label)) {
            return console.warn(
              `[fts-select warn]: Label key "option.${this.label}" does not` +
                ` exist in options object ${JSON.stringify(option)}.\n` +
                'https://vue-select.org/api/props.html#getoptionlabel'
            )
          }
          return option[this.label];
        }
        return option;
      }
      const filter = (options, search) => {
        return options.filter((option) => {
          let label = getOptionLabel(option)
          if (typeof label === 'number') {
            label = label.toString();
          }

          if (this.isFilterKeys) {
            return this.filterByKeys(option, label, search);
          }

          return this.filterBy(option, label, search);
        });
      }
      // end
      let options = this.search.length ?
        filter(this.options, this.search) :
        this.options;

      return options;
    },
    /**
     * infinite scroll
     */
    paginated() {
      return this.filtered.slice(0, this.totalCount);
    },
    /**
     * infinite scroll
     */
    hasNextPage() {
      return this.paginated.length < this.filtered.length;
    }
  },
  beforeDestroy() {
    infiniteScrollDisconnect(this.infiniteScrollObserver);
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-select {
  &:not(.custom) {
    .vs__dropdown-toggle {
      min-width: 0;
      min-height: $form-control-height;
      padding: 0;
      border: 1px solid var(--fts-form-control-border);
      border-radius: 0;
      background-color: var(--fts-form-control-background);
      color: var(--fts-form-control);
      font-size: $font-size;
      line-height: $line-height;
      &:focus-within {
        border-color: var(--fts-primary);
        outline: none;
        -webkit-box-shadow: $input-box-shadow;
        box-shadow: $input-box-shadow;
      }
    }
    .vs__selected-options {
      min-width: 0;
      padding: 0;
      align-items: center;
      .selected-option {
        min-width: 0;
        padding: 0.375rem 0.75rem;  
      }
      .vs__search {
        margin: 0;
        padding: 0.375rem 0.75rem;
        border: 0;
        font-size: $font-size;
        line-height: $line-height;
      }
    }
    .vs__actions {
      padding: 0 0.75rem;
      .vs__clear {
        margin-right: 0.5rem;
        fill: var(--fts-default);
      }
      .vs__open-indicator {
        fill: var(--fts-default);
      }
      .select-open-indicator {
        color: var(--fts-default);
        font-size: 1rem;
        &:hover {
          cursor: pointer;
        }
      }
    }
    &.vs--single {
      &.vs--open {
        .selected-option {
          position: absolute;
          top: 0;
          left: 0;
          right: 0.75rem;
          opacity: 0.4;
          margin: auto;
        }
      }
      &.vs--searching {
        .selected-option {
          display: none;
        }
      }
      .vs__selected-options {
        flex-wrap: nowrap;
        .selected-option {
          > div {
            @include text-overflow-ellipsis;
          }
        }
      }
    }
    &.vs--multiple {
      .vs__selected-options {
        @include flex-gap($gapTop: 0.25rem);
        align-items: flex-start;
        padding-top: 0.25rem;
        padding-left: calc(0.75rem - 0.25rem);
        padding-right: calc(0.75rem - 0.25rem);
        .selected-option { 
          display: flex;
          align-items: center;
          margin-left: 0.25rem;
          padding: calc(0.375rem - 0.25rem) 0.25rem;
          @include theme {
            background-color: rgba(theme-get('fts-gray-100'), 0.75);
          }
          .deselect-icon {
            margin-left: 0.5rem;
            margin-right: 0.25rem;
            color: var(--fts-default);
            font-size: 0.75rem;
            &:hover {
              color: var(--fts-danger);
              cursor: pointer;
            }
          }
        }
      }
      .vs__search {
        padding-top: calc(0.375rem - 0.25rem);
        padding-left: 0.25rem;
        padding-right: 0.25rem;
      }
    }
    &.comfort-design {
      .vs__dropdown-toggle {
        min-height: $comfort-form-control-height;
        border-radius: $comfort-border-radius;
        font-size: $comfort-font-size;
      }
      .vs__selected-options {
        .vs__search {
          @include placeholder($comfort-placeholder-font-size);
          font-size: $comfort-font-size;
        }
      }
      &.vs--multiple {
        .vs__selected-options {
          .selected-option {
            padding-top: calc(0.5rem - 0.25rem);
            padding-bottom: calc(0.5rem - 0.25rem);
            border-radius: $comfort-border-radius;
          }
          .vs__search {
            padding-top: calc(0.5rem - 0.25rem);
          }
        }
      }
    }
  }
  &.vs--error {
    .vs__dropdown-toggle {
      border-color: $input-error-border;
    }
  }
  &.vs--disabled {
    .vs__dropdown-toggle {
      border-color: var(--fts-disabled);
      background-color: var(--fts-disabled);
    }
    .vs__search {
      background-color: unset;
    }
  }
  .vs__dropdown-toggle {
    .vs__search {
      color: var(--fts-text);
      &::placeholder {
        color: var(--fts-form-control-placeholder);
      }
    }
  }
}
// infinite scroll loader
.fts-select__infinite-scroll-loader {
  padding: 0.25rem 0.5rem;
  font-size: $font-size;
  line-height: $line-height;
  text-align: center;
  color: var(--fts-text);
}
// override
.vs__dropdown-menu {
  .vs__dropdown-option {
    font-size: $font-size;
    color: var(--fts-text);
  }
  .search-no-options {
    .search-no-options-wrapper {
      font-size: $font-size;
      line-height: $line-height;
    }
  }
  &.comfort-design {
    .vs__dropdown-option {
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
      font-size: $comfort-font-size;
    }
    .search-no-options {
      .search-no-options-wrapper {
        font-size: $comfort-font-size;
        line-height: $comfort-line-height;
      }
    }
  }
  box-shadow: var(--fts-elevation-3);
  border: 0;
  border-radius: .25rem;
  background: var(--fts-popover-alt-background);
  color: var(--fts-text);
  z-index: 1070 !important;
  .vs__dropdown-option--highlight {
    &:not(.vs__dropdown-option--disabled),
    &:not(.vs__dropdown-option--selected) {
      background-color: rgba(var(--fts-gray-100-rgb), 0.75);
    }
  }
  .vs__dropdown-option--disabled {
    &:not(.vs__dropdown-option--selected) {
      background-color: transparent;
    }
    &:not(:has(*)),
    > *:not([data-option="error"]) {
      opacity: 0.5;
    }
  }
  .search-no-options {
    @include flex-center-middle;
    flex-direction: column;
    padding: 0.375rem 0.75rem;
    color: var(--fts-default);
    .search-no-options-wrapper {
      display: flex;
      align-items: center;
    }
    .search-no-options-icon {
      margin-right: 0.5rem;
    }
  }
}
</style>